import { createSmoothScroll } from "zyra";
import "@zyra/scroll/styles.css";

/** Opt into Zyra's managed smooth scroller. */
export function setupExperience(root: HTMLElement): () => void {
  const preference = matchMedia("(prefers-reduced-motion: reduce)");
  const smooth = createSmoothScroll({ lerp: 0.085 });
  let raf = 0;
  const animations = new Set<Animation>();
  const scene = root.querySelector<HTMLElement>(".scroll-scene");
  const demo = root.querySelector<HTMLElement>(".motion-playground");
  const sculpture = root.querySelector<HTMLElement>(".sculpture");
  const progress = root.querySelector<HTMLElement>(".reading-progress");
  const configure = () => {
    root.dataset.smoothing = preference.matches ? "disabled" : "enabled";
    if (preference.matches)
      for (const animation of animations) animation.finish();
    schedule();
  };
  const measure = () => {
    raf = 0;
    const amount =
      window.scrollY /
      Math.max(1, document.documentElement.scrollHeight - innerHeight);
    if (progress) progress.style.transform = `scaleX(${amount})`;
    if (demo) {
      const box = demo.getBoundingClientRect();
      const value = Math.max(
        0,
        Math.min(1, (innerHeight - box.top) / (innerHeight + box.height)),
      );
      demo.style.setProperty(
        "--demo-scroll",
        preference.matches ? ".5" : String(value),
      );
    }
    if (sculpture)
      sculpture.style.setProperty(
        "--parallax",
        `${preference.matches ? 0 : Math.min(window.scrollY, innerHeight) * 0.12}px`,
      );
    if (scene) {
      const box = scene.getBoundingClientRect();
      const value = Math.max(
        0,
        Math.min(1, -box.top / Math.max(1, box.height - innerHeight)),
      );
      scene.style.setProperty(
        "--scene",
        preference.matches ? ".5" : String(value),
      );
      const label = scene.querySelector("[data-scroll-percentage]");
      if (label)
        label.textContent = `${Math.round(value * 100)
          .toString()
          .padStart(2, "0")}%`;
    }
  };
  function schedule() {
    if (!raf) raf = requestAnimationFrame(measure);
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const element = entry.target as HTMLElement;
        if (!preference.matches && element.animate) {
          const animation = element.animate(
            [
              { opacity: 0, transform: "translateY(28px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 850,
              delay: Number(element.dataset.delay ?? 0),
              easing: "cubic-bezier(.16,1,.3,1)",
              fill: "backwards",
            },
          );
          animations.add(animation);
          void animation.finished.then(
            () => animations.delete(animation),
            () => animations.delete(animation),
          );
        }
        observer.unobserve(element);
      }
    },
    { threshold: 0.12 },
  );
  root
    .querySelectorAll(".reveal")
    .forEach((element) => observer.observe(element));
  const magnetic = [...root.querySelectorAll<HTMLElement>("[data-magnetic]")];
  const move = (event: PointerEvent) => {
    if (preference.matches || event.pointerType === "touch") return;
    const element = event.currentTarget as HTMLElement,
      box = element.getBoundingClientRect();
    element.style.setProperty(
      "--mx",
      `${(event.clientX - box.left - box.width / 2) * 0.09}px`,
    );
    element.style.setProperty(
      "--my",
      `${(event.clientY - box.top - box.height / 2) * 0.09}px`,
    );
  };
  const reset = (event: PointerEvent) => {
    const element = event.currentTarget as HTMLElement;
    element.style.setProperty("--mx", "0px");
    element.style.setProperty("--my", "0px");
  };
  magnetic.forEach((element) => {
    element.addEventListener("pointermove", move);
    element.addEventListener("pointerleave", reset);
  });
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
  preference.addEventListener("change", configure);
  configure();
  return () => {
    smooth.destroy();
    observer.disconnect();
    cancelAnimationFrame(raf);
    animations.forEach((animation) => animation.cancel());
    window.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    preference.removeEventListener("change", configure);
    magnetic.forEach((element) => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", reset);
    });
  };
}
